import styled, { css } from "styled-components";
import { theme } from "../../theme/toyotaTheme";
import * as Flex from "../../globals/Flex";
import * as Button from "../../components/Button";
import { ImageFluid } from "../../globals/Image";
import * as MonthlyPrice from "../price/MonthlyPrice";
import * as CashPrice from "../price/CashPrice";
import { getBreakpoint } from "../../../../../themes/common";
import { CtaIconButton } from "../../../../toyota/CtaButton";
import { parseHexToRGB } from "../../../../../utils";

export const Banner = styled.div`
    position: relative;
    width: calc(100% + 48px); /* 48px => spacing left and right */
    height: 170px;
    margin: -${theme.space[3]}px -${theme.space[3]}px ${theme.space[3]}px;
    border-radius: 4px 4px 0 0;
    overflow: hidden;
`;

export const Image = styled(ImageFluid)`
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: auto;
    border-radius: 4px 4px 0 0;
    transform: translateY(-50%);
`;

export const ServiceWrapper = styled.div<{ selected?: boolean }>`
    margin: ${theme.space[4]}px 0;
    padding: ${theme.space[3]}px;
    background: ${theme.colors.primaryWhite};
    border-radius: 4px;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.25);
    &:first-child {
        margin-top: 0px;
    }

    ${({ selected }) =>
        selected &&
        css`
            border: 1.5px solid ${theme.colors.primaryBlack};
            box-shadow: none;
        `};
`;

export const Header = styled.div`
    display: flex;
    flex-direction: column;
`;

export const TitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
`;

export const Title = styled.h2.attrs(() => ({
    className: "t-sub-epsilon-text",
}))``;

export const Description = styled.div.attrs(() => ({
    className: "t-base-text",
}))`
    margin-bottom: ${theme.space[3]}px;
`;

export const List = styled(Flex.Row)`
    justify-content: space-between;
    margin: ${theme.space[1]}px 0;
    padding-bottom: ${theme.space[1]}px;
    color: ${theme.colors.grey3};
    border-bottom: 1px solid ${theme.colors.grey2};

    strong {
        font-family: ${theme.fonts.regular};
        font-weight: normal;
    }
`;

export const Row = styled(Flex.Row)`
    justify-content: space-between;
    margin: ${theme.space[3]}px 0;
`;

export const Col = styled(Flex.Col)`
    padding-left: 0;

    html[dir="rtl"] & {
        padding-right: 0;
    }
`;

export const Panel = styled.div<{ isLoading?: boolean }>`
    position: relative;
    padding: ${theme.space[3]}px;
    background-color: ${theme.colors.grey1};
    border-radius: 4px;

    ${({ isLoading }) =>
        isLoading &&
        css`
            height: 85px;
            margin-bottom: ${theme.space[3]}px;
        `}
`;

export const PanelFooter = styled(Flex.Row)`
    justify-content: space-between;
    margin: ${theme.space[3]}px ${theme.space[3]}px 0 0;
    padding-top: ${theme.space[2]}px;

    ${MonthlyPrice.Text} {
        font-family: ${theme.fonts.regular};
        font-size: 1.7rem;
        color: ${theme.colors.primaryBlack};
    }

    ${CtaIconButton} {
        border-color: transparent;
    }
`;

export const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;
    margin-top: ${theme.space[3]}px;
    @media ${getBreakpoint("up", "md")} {
        margin-top: 0;
    }
`;

export const Toggle = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    margin-top: ${theme.space[5]}px;
`;

export const ToggleText = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    &:hover {
        text-decoration: underline;
        cursor: pointer;
    }
`;

export const TradeInWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-top: ${theme.space[2]}px;

    ${CashPrice.Text} {
        font-size: 1.6rem;
        line-height: 2.4rem;
        color: ${theme.colors.primaryBlack};
    }

    ${MonthlyPrice.Text} {
        font-size: 1.6rem;
        line-height: 2.4rem;
        color: ${theme.colors.grey7};
    }
`;

export const RemoveButton = styled(Button.Link)`
    margin-left: ${theme.space[1]}px;

    html[dir="rtl"] & {
        margin-left: 0;
        margin-right: ${theme.space[1]}px;
    }
`;

export const ListWrapper = styled.div`
    margin-top: ${theme.space[3]}px;
    strong {
        color: ${theme.colors.grey4};
    }
`;

export const ButtonWrapper = styled.div`
    display: flex;
    flex-grow: 1;
    max-width: 100%;
`;

export const MonthlyPriceWrapper = styled.strong`
    margin-bottom: ${theme.space[3]}px;
    ${MonthlyPrice.Text} {
        font-family: ${theme.fonts.regular};
    }
`;

export const PriceWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;

    ${CashPrice.Text} {
        font-size: 1.6rem;
        line-height: 2.4rem;
        color: ${theme.colors.primaryBlack};
    }

    ${MonthlyPrice.Text} {
        margin-right: ${theme.space[2]}px;
        font-size: 1.6rem;
        line-height: 2.4rem;
        color: ${theme.colors.primaryBlack};
    }
`;

export const IntroductionWrapper = styled.div`
    margin-top: ${theme.space[5]}px;
`;

export const IntroductionDescription = styled.div`
    margin: 8px 0;
    color: ${theme.colors.grey4};
`;

export const ServicePlanDescription = styled.div`
    background-color: ${theme.colors.grey1};

    span {
        display: block;
        width: 100%;
    }

    table {
        width: 100%;
        margin-bottom: ${theme.space[3]}px;
        padding: ${theme.space[2]}px 0;
    }

    td {
        padding: ${theme.space[1]}px ${theme.space[3]}px;
        font-size: 1.6rem;
        line-height: 2.4rem;
        &:first-child {
            width: 40%;
            font-family: ${theme.fonts.regular};
            color: ${theme.colors.primaryBlack};
        }
        &:last-child {
            width: 60%;
            font-family: ${theme.fonts.base};
            color: ${theme.colors.primaryBlack};
        }
    }

    tr:first-child td {
        padding-top: ${theme.space[3]}px;
    }

    tr:last-child td {
        padding-bottom: ${theme.space[3]}px;
    }
`;

export const Table = styled.table`
    width: 100%;
    padding: ${theme.space[2]}px 0;
    td {
        font-size: 1.6rem;
        line-height: 2.4rem;
        vertical-align: top;
        &:first-child {
            width: 40%;
            font-family: ${theme.fonts.regular};
            color: ${theme.colors.primaryBlack};
        }
        &:last-child {
            width: 60%;
            font-family: ${theme.fonts.base};
            color: ${theme.colors.primaryBlack};
        }
    }
`;

export const ServiceTags = styled.div`
    display: flex;
    flex-direction: row;
    align-self: flex-start;
    justify-content: flex-end;
    margin-top: ${theme.space[1]}px;
    margin-bottom: ${theme.space[2]}px;

    @media ${getBreakpoint("up", "lg")} {
        margin-top: 0;
    }
`;

export const ServiceTag = styled.span.attrs(() => ({
    className: "t-milli-text",
}))`
    margin: 0 ${theme.space[1]}px;
    padding: ${theme.space[1]}px ${theme.space[2]}px;
    color: ${theme.colors.primaryWhite};
    white-space: nowrap;
    background-color: rgba(${parseHexToRGB(theme.colors.primaryBlack).rgb}, 0.8);
    border-radius: 20px;
    pointer-events: none;

    &:first-child {
        margin-left: 0;
    }

    &:last-child {
        margin-right: 0;
    }

    @media ${getBreakpoint("up", "lg")} {
        margin: 0 ${theme.space[1]}px;
    }

    html[dir="rtl"] & {
        &:first-child {
            margin-right: 0;
        }
        &:last-child {
            margin-left: 0;
        }
    }
`;

export const Footnote = styled(Button.Link)`
    font-size: 1.3rem;
    line-height: 2rem;
    color: ${theme.colors.grey4};
`;

export const FinancePacksList = styled.ul`
    padding-left: 20px;

    html[dir="rtl"] & {
        padding-left: 0;
        padding-right: 20px;
    }
`;

export const FinancePacksGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: ${theme.space[3]}px;
    row-gap: ${theme.space[3]}px;

    ${FinancePacksList} + & {
        margin-top: ${theme.space[3]}px;
    }
`;

export const FinancePackItem = styled.div`
    display: flex;
`;

export const FinancePackItemImage = styled.div`
    max-width: 80px;
    max-height: 80px;
    margin-right: ${theme.space[3]}px;
    object-fit: cover;

    img {
        min-width: 100%;
        max-width: 100%;
    }

    html[dir="rtl"] & {
        margin-left: ${theme.space[3]}px;
        margin-right: 0;
    }
`;

export const FinancePackItemContent = styled.div`
    display: flex;
    flex-direction: column;

    ${CashPrice.Text},
    ${MonthlyPrice.Text} {
        font-family: ${theme.fonts.base};
    }
`;

export const FinancePackItemTitle = styled.h2`
    margin: 0;
    font-family: ${theme.fonts.regular};
    font-size: 1.6rem;
    text-transform: none;
`;
